import type { ButtonHTMLAttributes, ReactNode } from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'accent' | 'outline' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: string;
  children?: ReactNode;
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: 'bg-primary-500 text-background-50 hover:bg-primary-600 focus-visible:ring-primary-300',
  secondary:
    'bg-secondary-500 text-background-50 hover:bg-secondary-600 focus-visible:ring-secondary-300',
  accent: 'bg-accent-500 text-background-50 hover:bg-accent-600 focus-visible:ring-accent-300',
  outline:
    'border border-background-300 text-foreground-900 hover:bg-background-100 focus-visible:ring-background-300',
  ghost: 'text-foreground-800 hover:bg-background-100 focus-visible:ring-background-300',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm gap-1.5',
  md: 'px-4 py-2 text-sm gap-2',
  lg: 'px-6 py-3 text-base gap-2.5',
};

export default function Button({
  variant = 'primary',
  size = 'md',
  icon,
  children,
  className = '',
  type = 'button',
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled}
      className={`inline-flex items-center justify-center rounded-md font-label font-semibold whitespace-nowrap transition-colors duration-150 focus:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50 ${
        disabled ? '' : 'cursor-pointer'
      } ${VARIANT_CLASSES[variant]} ${SIZE_CLASSES[size]} ${className}`}
      {...rest}
    >
      {icon ? <i aria-hidden="true" className={`${icon} w-4 h-4 flex items-center justify-center`} /> : null}
      {children}
    </button>
  );
}